import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, LogOut, Save, BadgeCheck, Upload, X, Instagram, Youtube, Check, MapPin, Loader2, Users, IndianRupee, Crown, Sparkles, Edit2, Plus, Camera, Film, Music, Mic, PenTool, Smartphone, CheckCircle, Share2, Clapperboard, Gamepad2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BottomNav from "@/components/BottomNav";
import ShareProfileDialog from "@/components/ShareProfileDialog";
import { useAuth, API } from "@/App";

const InfluencerProfile = () => {
  const { user, logout, getAuthHeaders, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [portfolio, setPortfolio] = useState({ items: [] });
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [trustScore, setTrustScore] = useState(null);
  const fileInputRef = useRef(null);
  const portfolioInputRef = useRef(null);
  
  // Star Profile states
  const [talentProfile, setTalentProfile] = useState(null);
  const [showTalentModal, setShowTalentModal] = useState(false);
  const [savingTalent, setSavingTalent] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState({});
  const [talentForm, setTalentForm] = useState({
    categories: [], // Multiple categories for Pro Creators
    category: "",   // Primary category (backward compatibility)
    bio: "",
    hourly_rate: "",
    project_rate: "",
    languages: ["Hindi", "English"],
    experience: "",
    city: "",
    // Precise location (auto-detected via Nominatim)
    location: "",
    village: "",
    district: "",
    state: "",
    latitude: null,
    longitude: null,
    photos: [],
    video_showreel: "",
    portfolio_links: [""],
    availability: "available",
    instagram_handle: "",
    youtube_channel: "",
    facebook_page: "",
    followers_count: ""
  });
  const [detectingTalentLocation, setDetectingTalentLocation] = useState(false);
  
  // Coupon code state for Pro Creator upgrade
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    niches: [],
    location: "",
    city: "",
    state: "",
    district: "",
    village: "",
    latitude: null,
    longitude: null
  });

  const allNiches = ["Fashion", "Tech", "Food", "Fitness", "Beauty", "Travel", "Lifestyle", "Comedy", "Education", "Gaming", "Finance", "Health", "Music", "Art", "Sports"];

  // Auto-detect location using browser geolocation + Nominatim (FREE)
  const detectLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setDetectingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Use Nominatim (OpenStreetMap) for reverse geocoding - FREE!
          // zoom=18 for precise location (village/neighbourhood level)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en'
              }
            }
          );
          const data = await response.json();
          
          // Extract precise location - village > suburb > town > city
          const address = data.address || {};
          const village = address.village || address.hamlet || address.neighbourhood || "";
          const suburb = address.suburb || "";
          const town = address.town || "";
          const city = address.city || address.municipality || "";
          const district = address.state_district || address.county || "";
          const state = address.state || "";
          
          // Build precise location string
          let preciseParts = [];
          if (village) preciseParts.push(village);
          else if (suburb) preciseParts.push(suburb);
          else if (town) preciseParts.push(town);
          
          if (city && city !== village && city !== suburb) preciseParts.push(city);
          if (district && !preciseParts.includes(district)) preciseParts.push(district);
          if (state) preciseParts.push(state);
          
          const locationDisplay = preciseParts.slice(0, 3).join(", ") || "Location detected";
          const cityValue = village || suburb || town || city || "";
          
          setFormData(prev => ({
            ...prev,
            location: locationDisplay,
            city: cityValue,
            state: state,
            district: district,
            village: village,
            latitude: latitude,
            longitude: longitude
          }));
          
          toast.success(`Location: ${locationDisplay}`);
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          // Still save coordinates even if reverse geocoding fails
          setFormData(prev => ({
            ...prev,
            latitude: latitude,
            longitude: longitude,
            location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
          }));
          toast.success("Location coordinates saved");
        }
        
        setDetectingLocation(false);
      },
      (error) => {
        setDetectingLocation(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Please allow location access in your browser settings");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information unavailable");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out");
            break;
          default:
            toast.error("Failed to get location");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Detect precise location for Talent / Star profile form (writes to talentForm)
  const detectTalentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setDetectingTalentLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await response.json();
          const address = data.address || {};
          const village = address.village || address.hamlet || address.neighbourhood || "";
          const suburb = address.suburb || "";
          const town = address.town || "";
          const city = address.city || address.municipality || "";
          const district = address.state_district || address.county || "";
          const state = address.state || "";

          let preciseParts = [];
          if (village) preciseParts.push(village);
          else if (suburb) preciseParts.push(suburb);
          else if (town) preciseParts.push(town);
          if (city && city !== village && city !== suburb) preciseParts.push(city);
          if (district && !preciseParts.includes(district)) preciseParts.push(district);
          if (state) preciseParts.push(state);
          const locationDisplay = preciseParts.slice(0, 3).join(", ") || "Location detected";
          const cityValue = village || suburb || town || city || "";

          setTalentForm(prev => ({
            ...prev,
            location: locationDisplay,
            city: cityValue,
            village: village || suburb || "",
            district: district,
            state: state,
            latitude: latitude,
            longitude: longitude,
          }));
          toast.success(`Location: ${locationDisplay}`);
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          setTalentForm(prev => ({
            ...prev,
            latitude: latitude,
            longitude: longitude,
            location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          }));
          toast.success("Location coordinates saved");
        }
        setDetectingTalentLocation(false);
      },
      (error) => {
        setDetectingTalentLocation(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Please allow location access in your browser settings");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information unavailable");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out");
            break;
          default:
            toast.error("Failed to get location");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    fetchProfile();
    fetchPortfolio();
    fetchTrustScore();
    fetchTalentProfile();
    fetchCategories();
  }, []);

  const fetchTalentProfile = async () => {
    try {
      const response = await axios.get(`${API}/talent/profile`, { headers: getAuthHeaders() });
      if (response.data.has_profile) {
        setTalentProfile(response.data.profile);
        // Pre-fill form
        const p = response.data.profile;
        // Handle both single category and multiple categories
        const existingCategories = p.categories || (p.category ? [p.category] : []);
        setTalentForm({
          categories: existingCategories,
          category: p.category || existingCategories[0] || "",
          bio: p.bio || "",
          hourly_rate: p.hourly_rate || "",
          project_rate: p.project_rate || "",
          languages: p.languages || ["Hindi", "English"],
          experience: p.experience || "",
          city: p.city || "",
          location: p.location || "",
          village: p.village || "",
          district: p.district || "",
          state: p.state || "",
          latitude: p.latitude || null,
          longitude: p.longitude || null,
          photos: p.photos || [],
          video_showreel: p.video_showreel || "",
          portfolio_links: p.portfolio_links?.length > 0 ? p.portfolio_links : [""],
          availability: p.availability || "available",
          instagram_handle: p.instagram_handle || "",
          youtube_channel: p.youtube_channel || "",
          facebook_page: p.facebook_page || "",
          followers_count: p.followers_count || ""
        });
      }
    } catch (error) {
      console.error("Failed to fetch talent profile");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/talent/categories`);
      setCategories(response.data.categories || []);
      setPricing(response.data.pricing || {});
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  const saveTalentProfile = async () => {
    if (talentForm.categories.length === 0) {
      toast.error("Please select at least one category");
      return;
    }
    if (!talentForm.bio || talentForm.bio.length < 20) {
      toast.error("Please write a bio (min 20 characters)");
      return;
    }
    if (!talentForm.hourly_rate || parseInt(talentForm.hourly_rate) < 100) {
      toast.error("Hourly rate must be at least ₹100");
      return;
    }
    
    setSavingTalent(true);
    try {
      const payload = {
        category: talentForm.categories[0], // Primary category
        categories: talentForm.categories,  // All selected categories
        bio: talentForm.bio,
        hourly_rate: parseInt(talentForm.hourly_rate),
        project_rate: parseInt(talentForm.project_rate) || parseInt(talentForm.hourly_rate) * 5,
        languages: talentForm.languages,
        experience: talentForm.experience,
        city: talentForm.city,
        location: talentForm.location || "",
        village: talentForm.village || "",
        district: talentForm.district || "",
        state: talentForm.state || "",
        latitude: talentForm.latitude,
        longitude: talentForm.longitude,
        portfolio_links: talentForm.portfolio_links.filter(l => l.trim() !== ""),
        availability: talentForm.availability,
        photos: talentForm.photos,
        video_showreel: talentForm.video_showreel,
        instagram_handle: talentForm.instagram_handle,
        youtube_channel: talentForm.youtube_channel,
        facebook_page: talentForm.facebook_page,
        followers_count: parseInt(talentForm.followers_count) || 0
      };
      
      await axios.post(`${API}/talent/profile`, payload, { headers: getAuthHeaders() });
      toast.success("🎉 Star Profile saved! You're now visible to brands.");
      setShowTalentModal(false);
      fetchTalentProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingTalent(false);
    }
  };

  const handleVerifiedBadgeUpgrade = async () => {
    try {
      // Include promo code if applied
      const params = promoApplied ? `?promo_code=${encodeURIComponent(promoApplied.code)}` : '';
      const response = await axios.post(`${API}/talent/purchase-verified-badge${params}`, {}, { headers: getAuthHeaders() });
      
      const options = {
        key: response.data.key_id,
        amount: response.data.amount_paise,
        currency: response.data.currency || "INR",
        name: "FOUNDSTAR",
        description: promoApplied 
          ? `Pro Creator Badge (${promoApplied.discount_percent}% OFF with ${promoApplied.code})` 
          : "Pro Creator Badge",
        order_id: response.data.order_id,
        handler: async (paymentResponse) => {
          try {
            await axios.post(`${API}/talent/verify-badge-payment`, {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature
            }, { headers: getAuthHeaders() });
            
            toast.success("🎉 Payment Successful! Your verification request has been submitted.");
            // Reset promo state
            setPromoCode("");
            setPromoApplied(null);
            setDiscountedPrice(null);
            fetchTalentProfile();
          } catch (error) {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          email: user?.email || "",
          name: user?.name || talentProfile?.name || ""
        },
        theme: { color: "#7B2FBE" }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    }
  };

  // Validate promo code for Pro Creator upgrade
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    setPromoValidating(true);
    try {
      const response = await axios.post(`${API}/talent/validate-promo-code`, 
        { promo_code: promoCode.trim() },
        { headers: getAuthHeaders() }
      );
      
      setPromoApplied(response.data);
      setDiscountedPrice(response.data.final_price);
      toast.success(`🎉 Promo applied! You save ₹${response.data.discount_amount}`);
    } catch (error) {
      setPromoApplied(null);
      setDiscountedPrice(null);
      toast.error(error.response?.data?.detail || "Invalid promo code");
    } finally {
      setPromoValidating(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode("");
    setPromoApplied(null);
    setDiscountedPrice(null);
  };

  const getCategoryIcon = (id) => {
    const icons = { models: Camera, actors: Film, dancers: Music, voice_artists: Mic, photographers: Camera, content_writers: PenTool, influencers: Smartphone, editors: Clapperboard, gaming: Gamepad2 };
    return icons[id] || Camera;
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/influencer/profile`, { 
        
        headers: getAuthHeaders()
      });
      setProfile(response.data);
      
      // Handle both old (single) and new (array) formats
      let niches = response.data.niches || [];
      if (niches.length === 0 && response.data.niche) {
        niches = [response.data.niche];
      }
      
      let platforms = response.data.platforms || [];
      if (platforms.length === 0 && response.data.platform) {
        if (response.data.platform === "Both") {
          platforms = ["Instagram", "YouTube"];
        } else {
          platforms = [response.data.platform];
        }
      }
      
      setFormData({
        name: response.data.name || "",
        niches: niches,
        location: response.data.location || "",
        city: response.data.city || "",
        state: response.data.state || "",
        district: response.data.district || "",
        village: response.data.village || "",
        latitude: response.data.latitude || null,
        longitude: response.data.longitude || null
      });
      
      if (response.data.picture) {
        loadProfileImage(response.data.picture);
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/influencer/portfolio`, {
        headers: getAuthHeaders()
      });
      setPortfolio(response.data);
    } catch (error) {
      console.error("Failed to load portfolio");
    }
  };

  const fetchTrustScore = async () => {
    if (!user?.user_id) return;
    try {
      const response = await axios.get(`${API}/influencer/${user.user_id}/trust-score`);
      setTrustScore(response.data);
    } catch (error) {
      console.error("Failed to load trust score");
    }
  };

  const handlePortfolioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only images and videos allowed");
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Max 50MB");
      return;
    }

    setUploadingPortfolio(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/influencer/portfolio/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success("Portfolio item uploaded!");
      fetchPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally {
      setUploadingPortfolio(false);
      if (portfolioInputRef.current) {
        portfolioInputRef.current.value = '';
      }
    }
  };

  const deletePortfolioItem = async (itemId) => {
    try {
      await axios.delete(`${API}/influencer/portfolio/${itemId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Item deleted");
      fetchPortfolio();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const loadProfileImage = async (picturePath) => {
    if (picturePath.startsWith("http")) {
      setProfileImageUrl(picturePath);
    } else {
      try {
        const response = await axios.get(`${API}/files/${picturePath}`, {
          
          headers: getAuthHeaders(),
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        setProfileImageUrl(blobUrl);
      } catch (error) {
        console.error("Failed to load profile image:", error);
        setProfileImageUrl(null);
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image (JPEG, PNG, GIF, or WebP)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);
      
      await axios.post(`${API}/upload/profile-photo`, uploadData, {
        
        headers: { 
          "Content-Type": "multipart/form-data",
          ...getAuthHeaders()
        }
      });
      
      toast.success("Profile photo updated!");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleNiche = (niche) => {
    const isPro = !!talentProfile?.is_verified;
    setFormData(prev => {
      const isSelected = prev.niches.includes(niche);

      // Always allow removing an already-selected niche
      if (isSelected) {
        return { ...prev, niches: prev.niches.filter(n => n !== niche) };
      }

      // Adding a new niche when there is already 1 selected → Pro only
      if (!isPro && prev.niches.length >= 1) {
        // Defer the toast to next tick so it's not called inside the updater
        setTimeout(() => {
          toast("🌟 Pro Feature!", {
            description: "Multiple niches require Pro Creator",
            action: {
              label: "Upgrade ₹199",
              onClick: () => handleVerifiedBadgeUpgrade(),
            },
          });
        }, 0);
        return prev; // ⛔ block the add
      }

      return { ...prev, niches: [...prev.niches, niche] };
    });
  };

  const handleSave = async () => {
    if (formData.niches.length === 0) {
      toast.error("Please select at least one niche");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/influencer/profile`, {
        name: formData.name,
        niches: formData.niches,
        niche: formData.niches[0], // Keep backward compatibility
        location: formData.location,
        city: formData.city,
        state: formData.state,
        district: formData.district,
        village: formData.village,
        latitude: formData.latitude,
        longitude: formData.longitude
      }, { 
        
        headers: getAuthHeaders()
      });
      setProfile(response.data);
      // Refresh user context (so user.name updates everywhere — share card, header, etc.)
      try { await checkAuth(); } catch (e) { /* ignore */ }
      // Refresh talent profile (so Star Profile share card sees the new name)
      try { await fetchTalentProfile(); } catch (e) { /* ignore */ }
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  useEffect(() => {
    return () => {
      if (profileImageUrl && profileImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, [profileImageUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF69B4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-nav">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">My Profile</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleLogout}
            className="text-[#64748B] hover:text-[#FF69B4]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Profile Photo Section */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              {/* Large Rounded Image like Star Cards */}
              <div className="w-52 h-60 rounded-2xl overflow-hidden border-4 border-[#E2E8F0] shadow-lg">
                {profileImageUrl ? (
                  <img 
                    src={profileImageUrl} 
                    alt={formData.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#FF69B4] to-[#87CEEB] flex items-center justify-center">
                    <span className="text-white font-outfit font-bold text-6xl">
                      {formData.name?.charAt(0) || "U"}
                    </span>
                  </div>
                )}
              </div>
              {profile?.is_verified && (
                <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center border-3 border-white shadow-md">
                  <BadgeCheck className="w-7 h-7 text-white" />
                </div>
              )}
            </div>
            <h2 className="font-outfit font-semibold text-xl text-[#1E293B]">{formData.name}</h2>
            {profile?.is_verified && (
              <span className="text-sm text-[#87CEEB] font-medium">Verified Influencer ✨</span>
            )}
          </div>

          {/* Upload Photo Button */}
          <div className="mt-6">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              data-testid="profile-photo-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full rounded-xl border-dashed border-2 border-[#E2E8F0] hover:border-[#FF69B4] hover:bg-[#FFF0F5] py-6"
              data-testid="upload-photo-btn"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#FF69B4] border-t-transparent rounded-full animate-spin mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2 text-[#FF69B4]" />
                  <span className="text-[#64748B]">Change Profile Photo</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Star Profile Card */}
        <Card className="bg-gradient-to-r from-[#7B2FBE] to-[#FF4D6D] text-white overflow-hidden mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="font-bold text-xl">Star Profile</h2>
                  <p className="text-white/80 text-sm">
                    {talentProfile ? categories.find(c => c.id === talentProfile.category)?.name || "Creator" : "Get discovered by brands"}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowTalentModal(true)}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                {talentProfile ? (
                  <><Edit2 className="w-4 h-4 mr-1" /> Edit</>
                ) : (
                  <><Plus className="w-4 h-4 mr-1" /> Create</>
                )}
              </Button>
            </div>

            {talentProfile ? (
              <>
                {/* Share Profile button row */}
                <div className="flex justify-end mb-3">
                  <Button
                    size="sm"
                    onClick={() => setShareDialogOpen(true)}
                    className="bg-white text-[#7B2FBE] hover:bg-white/90 font-semibold"
                    data-testid="profile-share-btn"
                  >
                    <Share2 className="w-4 h-4 mr-1" /> Share Profile
                  </Button>
                </div>
                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <Users className="w-5 h-5 mx-auto mb-1 text-white/80" />
                    <p className="font-bold text-lg">
                      {talentProfile.followers_count >= 1000000 
                        ? `${(talentProfile.followers_count / 1000000).toFixed(1)}M`
                        : talentProfile.followers_count >= 1000 
                          ? `${(talentProfile.followers_count / 1000).toFixed(0)}K`
                          : talentProfile.followers_count || "0"}
                    </p>
                    <p className="text-white/70 text-xs">Followers</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <Star className="w-5 h-5 mx-auto mb-1 text-yellow-300 fill-yellow-300" />
                    <p className="font-bold text-lg">{talentProfile.rating?.toFixed(1) || talentProfile.trust_score?.toFixed(1) || "New"}</p>
                    <p className="text-white/70 text-xs">Trust Score</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-300" />
                    <p className="font-bold text-lg">{talentProfile.bookings_completed || talentProfile.closed_deals || 0}</p>
                    <p className="text-white/70 text-xs">Completed</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <IndianRupee className="w-5 h-5 mx-auto mb-1 text-white/80" />
                    <p className="font-bold text-lg">₹{talentProfile.hourly_rate?.toLocaleString() || 0}</p>
                    <p className="text-white/70 text-xs">Per Hour</p>
                  </div>
                </div>
                
                {/* Social Links */}
                {(talentProfile.instagram_handle || talentProfile.youtube_channel) && (
                  <div className="flex gap-3">
                    {talentProfile.instagram_handle && (
                      <a href={`https://instagram.com/${talentProfile.instagram_handle.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-white/10 px-3 py-1.5 rounded-full text-sm hover:bg-white/20 transition-colors">
                        <Instagram className="w-4 h-4" />
                        {talentProfile.instagram_handle}
                      </a>
                    )}
                    {talentProfile.youtube_channel && (
                      <a href={talentProfile.youtube_channel} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-white/10 px-3 py-1.5 rounded-full text-sm hover:bg-white/20 transition-colors">
                        <Youtube className="w-4 h-4" />
                        YouTube
                      </a>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6 bg-white/10 rounded-xl">
                <Camera className="w-12 h-12 mx-auto mb-3 text-white/60" />
                <p className="font-medium mb-2">No Star Profile Yet</p>
                <p className="text-white/70 text-sm mb-4">Create your profile to get hired by brands</p>
                <Button
                  onClick={() => setShowTalentModal(true)}
                  className="bg-white text-[#7B2FBE] hover:bg-white/90"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Star Profile
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pro Creator Badge Upsell */}
        {talentProfile && !talentProfile.is_verified && (
          <Card className="border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-orange-50 mb-6">
            <CardContent className="p-4">
              {/* Main Info Row */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center shadow-lg">
                  <BadgeCheck className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[#1E293B]">Become Pro Creator</p>
                  <p className="text-sm text-gray-600">Get 3x more brand deals!</p>
                </div>
              </div>

              {/* Promo Code Section */}
              <div className="bg-white/70 rounded-xl p-3 mb-4">
                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Have a promo code?
                </p>
                {!promoApplied ? (
                  <div className="flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 h-9 text-sm uppercase"
                      data-testid="promo-code-input"
                    />
                    <Button 
                      onClick={validatePromoCode}
                      disabled={promoValidating || !promoCode.trim()}
                      size="sm"
                      className="bg-purple-500 hover:bg-purple-600 text-white h-9"
                      data-testid="apply-promo-btn"
                    >
                      {promoValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-green-700">{promoApplied.code}</span>
                      <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                        {promoApplied.discount_percent}% OFF
                      </span>
                    </div>
                    <button onClick={removePromoCode} className="text-gray-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Price & Pay Button */}
              <div className="flex items-center justify-between">
                <div>
                  {promoApplied ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-green-600">₹{discountedPrice}</span>
                      <span className="text-sm line-through text-gray-400">₹{pricing.verified_badge || 199}</span>
                      <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                        Save ₹{promoApplied.discount_amount}
                      </span>
                    </div>
                  ) : (
                    <span className="text-2xl font-bold text-[#1E293B]">₹{pricing.verified_badge || 199}</span>
                  )}
                </div>
                <Button 
                  onClick={handleVerifiedBadgeUpgrade}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-lg"
                  data-testid="upgrade-pro-btn"
                >
                  <Crown className="w-4 h-4 mr-1" />
                  Pay & Upgrade
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Details Form */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="font-outfit font-semibold text-lg text-[#1E293B] mb-4">Profile Details</h3>
          
          <div className="space-y-5">
            {/* Name */}
            <div>
              <Label className="text-[#1E293B]">Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1 rounded-xl"
                data-testid="profile-name-input"
              />
            </div>

            {/* Multiple Niches Selection — Pro Creators only */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#1E293B] block">
                  Select Your Niche{talentProfile?.is_verified ? "s (Multiple)" : ""}
                </Label>
                {talentProfile?.is_verified && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Pro: Multi-Niche
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {allNiches.map((niche) => (
                  <button
                    key={niche}
                    type="button"
                    onClick={() => toggleNiche(niche)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      formData.niches.includes(niche)
                        ? "bg-[#FF69B4] text-white"
                        : "bg-[#F8FAFC] text-[#64748B] hover:bg-[#FFF0F5] hover:text-[#FF69B4]"
                    }`}
                    data-testid={`niche-${niche}`}
                  >
                    {formData.niches.includes(niche) && <Check className="w-3 h-3 inline mr-1" />}
                    {niche}
                  </button>
                ))}
              </div>
              {formData.niches.length > 0 && (
                <p className="text-xs text-[#64748B] mt-2">
                  Selected: {formData.niches.join(", ")}
                </p>
              )}
              {/* Upsell for non-Pro users when they have 1 niche selected */}
              {!talentProfile?.is_verified && formData.niches.length === 1 && (
                <div className="mt-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                  <p className="text-xs text-yellow-800 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-600" />
                    <span><strong>Unlock Pro Creator</strong> to select multiple niches!</span>
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleVerifiedBadgeUpgrade}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs h-7"
                    >
                      {promoApplied 
                        ? `Upgrade — ₹${discountedPrice} (${promoApplied.discount_percent}% OFF)`
                        : `Upgrade to Pro — ₹${pricing.verified_badge || 199}`
                      }
                    </Button>
                    {promoApplied && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> {promoApplied.code} applied
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Location with Auto-Detect */}
            <div>
              <Label className="text-[#1E293B] flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#FF69B4]" />
                Location (Precise)
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="Click detect for precise location"
                  className="flex-1 rounded-xl"
                  data-testid="profile-location-input"
                />
                <Button
                  type="button"
                  onClick={detectLocation}
                  disabled={detectingLocation}
                  className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-xl px-4"
                  data-testid="detect-location-btn"
                >
                  {detectingLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <MapPin className="w-4 h-4 mr-1" />
                      Detect
                    </>
                  )}
                </Button>
              </div>
              {(formData.village || formData.city) && (
                <div className="mt-2 p-2 bg-[#F0F9FF] rounded-lg text-xs text-[#64748B]">
                  <p className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    <span className="font-medium">Detected:</span>
                  </p>
                  {formData.village && <p>Village/Area: {formData.village}</p>}
                  {formData.city && <p>City/Town: {formData.city}</p>}
                  {formData.district && <p>District: {formData.district}</p>}
                  {formData.state && <p>State: {formData.state}</p>}
                </div>
              )}
            </div>

            {/* Trust Score Display */}
            {trustScore && (
              <div className="bg-gradient-to-r from-[#FFF0F5] to-[#F0F9FF] rounded-2xl p-4">
                <Label className="text-[#1E293B] flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Your Trust Score
                </Label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold text-[#1E293B]">{trustScore.trust_score}</span>
                    <span className="text-lg text-[#64748B]">/5</span>
                    <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#64748B]">{trustScore.completed_deals} deals completed</p>
                    {trustScore.avg_rating && (
                      <p className="text-sm text-[#64748B]">{trustScore.total_ratings} reviews ({trustScore.avg_rating}⭐ avg)</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Trust Score increases with completed deals, good ratings, and profile completeness.
                </p>
              </div>
            )}

            <Button 
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#FF69B4] hover:bg-[#E05297] text-white rounded-full py-6"
              data-testid="profile-save-btn"
            >
              {saving ? "Saving..." : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </main>

      {/* Star Profile Modal */}
      <Dialog open={showTalentModal} onOpenChange={setShowTalentModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#7B2FBE] to-[#FF4D6D] rounded-t-lg"></div>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#7B2FBE]" />
              {talentProfile ? 'Edit Star Profile' : 'Create Star Profile'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category Selection - Multiple for Pro Creators */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">
                  Select Category {talentProfile?.is_verified ? '(Multiple)' : ''} *
                </Label>
                {talentProfile?.is_verified && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Pro: Multi-Category
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => {
                  const Icon = getCategoryIcon(cat.id);
                  const isSelected = talentForm.categories.includes(cat.id);
                  const isPro = talentProfile?.is_verified;
                  
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        if (isPro) {
                          // Pro Creator - toggle multiple categories
                          if (isSelected) {
                            setTalentForm({
                              ...talentForm, 
                              categories: talentForm.categories.filter(c => c !== cat.id),
                              category: talentForm.categories.filter(c => c !== cat.id)[0] || ""
                            });
                          } else {
                            setTalentForm({
                              ...talentForm, 
                              categories: [...talentForm.categories, cat.id],
                              category: talentForm.categories.length === 0 ? cat.id : talentForm.category
                            });
                          }
                        } else {
                          // Non-Pro - single category only
                          if (talentForm.categories.length >= 1 && !isSelected) {
                            toast("🌟 Pro Feature!", {
                              description: "Multiple categories require Pro Creator",
                              action: {
                                label: "Upgrade ₹199",
                                onClick: () => handleVerifiedBadgeUpgrade()
                              }
                            });
                            // Replace the current selection with the new category (non-Pro behavior)
                          }
                          setTalentForm({
                            ...talentForm,
                            categories: isSelected ? [] : [cat.id],
                            category: isSelected ? "" : cat.id
                          });
                        }
                      }}
                      className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 text-left relative ${
                        isSelected ? 'border-[#FF69B4] bg-pink-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF69B4] rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <Icon className="w-5 h-5" style={{ color: cat.color }} />
                      <div>
                        <p className="font-medium text-sm">{cat.name}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* Selected Categories Display */}
              {talentForm.categories.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {talentForm.categories.map(catId => {
                    const cat = categories.find(c => c.id === catId);
                    return cat ? (
                      <span key={catId} className="text-xs bg-[#FF69B4] text-white px-2 py-1 rounded-full">
                        {cat.name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              
              {/* Upsell for non-Pro users */}
              {!talentProfile?.is_verified && talentForm.categories.length === 1 && (
                <div className="mt-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                  <p className="text-xs text-yellow-800 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-600" />
                    <span><strong>Unlock Pro Creator</strong> to select multiple categories!</span>
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleVerifiedBadgeUpgrade}
                    className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-white text-xs h-7"
                  >
                    Upgrade to Pro — ₹{pricing.verified_badge || 199}
                  </Button>
                </div>
              )}
            </div>

            {/* Influencer-specific fields */}
            {talentForm.categories.includes('influencers') && (
              <div className="p-4 bg-pink-50 rounded-xl space-y-3">
                <p className="text-sm font-semibold text-[#E91E63] flex items-center gap-2">
                  <Smartphone className="w-4 h-4" /> Social Media Details
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Instagram Handle</Label>
                    <Input
                      placeholder="@yourusername"
                      value={talentForm.instagram_handle}
                      onChange={(e) => setTalentForm({...talentForm, instagram_handle: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Followers Count</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 10000"
                      value={talentForm.followers_count}
                      onChange={(e) => setTalentForm({...talentForm, followers_count: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">YouTube Channel (optional)</Label>
                  <Input
                    placeholder="Channel URL"
                    value={talentForm.youtube_channel}
                    onChange={(e) => setTalentForm({...talentForm, youtube_channel: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Bio */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Bio / About You *</Label>
              <Textarea
                placeholder="Describe your experience and what makes you unique..."
                value={talentForm.bio}
                onChange={(e) => setTalentForm({...talentForm, bio: e.target.value})}
                className="min-h-[100px]"
              />
            </div>

            {/* Precise Location with Auto-Detect */}
            <div>
              <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#FF69B4]" />
                Location (Precise)
              </Label>
              <div className="flex gap-2">
                <Input
                  value={talentForm.location || talentForm.city}
                  onChange={(e) => setTalentForm({...talentForm, location: e.target.value})}
                  placeholder="Click Detect for precise location"
                  className="flex-1"
                  data-testid="talent-location-input"
                />
                <Button
                  type="button"
                  onClick={detectTalentLocation}
                  disabled={detectingTalentLocation}
                  className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-xl px-4"
                  data-testid="talent-detect-location-btn"
                >
                  {detectingTalentLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <MapPin className="w-4 h-4 mr-1" />
                      Detect
                    </>
                  )}
                </Button>
              </div>
              {(talentForm.village || talentForm.city || talentForm.district || talentForm.state) && (
                <div className="mt-2 p-2 bg-[#F0F9FF] rounded-lg text-xs text-[#64748B]">
                  <p className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    <span className="font-medium">Detected:</span>
                  </p>
                  {talentForm.village && <p>Village/Area: {talentForm.village}</p>}
                  {talentForm.city && <p>City/Town: {talentForm.city}</p>}
                  {talentForm.district && <p>District: {talentForm.district}</p>}
                  {talentForm.state && <p>State: {talentForm.state}</p>}
                </div>
              )}
              {/* Manual city fallback */}
              <div className="mt-2">
                <Label className="text-[11px] text-[#94A3B8]">City (manual)</Label>
                <Input
                  placeholder="e.g., Mumbai"
                  value={talentForm.city}
                  onChange={(e) => setTalentForm({...talentForm, city: e.target.value})}
                  className="mt-1 h-9 text-sm"
                />
              </div>
            </div>

            {/* Rates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                  <IndianRupee className="w-4 h-4 text-[#00A86B]" /> Hourly Rate *
                </Label>
                <Input
                  type="number"
                  placeholder="e.g., 1000"
                  value={talentForm.hourly_rate}
                  onChange={(e) => setTalentForm({...talentForm, hourly_rate: e.target.value})}
                  min="100"
                />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                  <IndianRupee className="w-4 h-4 text-[#00A86B]" /> Project Rate
                </Label>
                <Input
                  type="number"
                  placeholder="e.g., 5000"
                  value={talentForm.project_rate}
                  onChange={(e) => setTalentForm({...talentForm, project_rate: e.target.value})}
                  min="500"
                />
              </div>
            </div>

            {/* Experience */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Experience</Label>
              <Select value={talentForm.experience} onValueChange={(v) => setTalentForm({...talentForm, experience: v})}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fresher">Fresher (0-1 year)</SelectItem>
                  <SelectItem value="1-3 years">1-3 years</SelectItem>
                  <SelectItem value="3-5 years">3-5 years</SelectItem>
                  <SelectItem value="5+ years">5+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Languages */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Languages</Label>
              <div className="flex flex-wrap gap-2">
                {['Hindi', 'English', 'Marathi', 'Gujarati', 'Tamil', 'Telugu', 'Bengali', 'Kannada'].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      const langs = talentForm.languages || [];
                      if (langs.includes(lang)) {
                        setTalentForm({...talentForm, languages: langs.filter(l => l !== lang)});
                      } else {
                        setTalentForm({...talentForm, languages: [...langs, lang]});
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-sm border transition-all ${
                      talentForm.languages?.includes(lang) 
                        ? 'bg-[#7B2FBE] text-white border-[#7B2FBE]' 
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#7B2FBE]'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            {/* Work Portfolio Upload - All Categories */}
            {talentForm.categories.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                <p className="text-sm font-semibold text-[#7B2FBE] flex items-center gap-2">
                  <Upload className="w-4 h-4" /> 
                  {talentForm.categories.includes('models') ? 'Upload Your Portfolio Photos' :
                   talentForm.categories.includes('actors') ? 'Upload Acting Clips / Headshots' :
                   talentForm.categories.includes('dancers') ? 'Upload Dance Videos / Photos' :
                   talentForm.categories.includes('photographers') ? 'Upload Your Best Shots' :
                   talentForm.categories.includes('voice_artists') ? 'Upload Voice Samples' :
                   talentForm.categories.includes('content_writers') ? 'Upload Writing Samples' :
                   'Upload Your Work'}
                </p>
                <p className="text-xs text-gray-500">
                  {talentForm.categories.includes('models') ? 'Show brands your best portfolio shots (max 30 photos)' :
                   talentForm.categories.includes('actors') ? 'Upload your showreel clips or headshots (max 30)' :
                   talentForm.categories.includes('dancers') ? 'Show your dance moves with videos/photos (max 30)' :
                   talentForm.categories.includes('photographers') ? 'Display your photography skills (max 30)' :
                   talentForm.categories.includes('voice_artists') ? 'Let brands hear your voice range (max 30 samples)' :
                   talentForm.categories.includes('content_writers') ? 'Share your writing portfolio (max 30)' :
                   'Upload images/videos of your work (max 30 files)'}
                </p>
                
                {/* Current Photos Grid */}
                {talentForm.photos?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {talentForm.photos.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                        <img 
                          src={photo.startsWith('http') ? photo : `${API}/files/${photo}`}
                          alt={`Work ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newPhotos = talentForm.photos.filter((_, i) => i !== index);
                            setTalentForm({...talentForm, photos: newPhotos});
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                {(!talentForm.photos || talentForm.photos.length < 30) && (
                  <div>
                    <input
                      type="file"
                      id="work-upload"
                      accept="image/*,video/*,.heic,.heif,.mov,.mp4,.m4v"
                      multiple
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        
                        const currentCount = talentForm.photos?.length || 0;
                        const allowedCount = 30 - currentCount;
                        
                        // Limit to max 10 files at a time for stability
                        const MAX_BATCH = 10;
                        if (files.length > MAX_BATCH) {
                          toast.error(`Please upload maximum ${MAX_BATCH} files at a time`);
                          return;
                        }
                        
                        if (files.length > allowedCount) {
                          toast.error(`You can only upload ${allowedCount} more files (max 30 total)`);
                          return;
                        }
                        
                        // Check file sizes (max 50MB images, 100MB videos)
                        const videoExtensions = ['mp4', 'mov', 'm4v', '3gp', 'mkv', 'webm', 'avi', 'mpeg'];
                        const oversizedFiles = files.filter(f => {
                          const ext = f.name.split('.').pop()?.toLowerCase() || '';
                          const isVideo = videoExtensions.includes(ext);
                          const maxSize = isVideo ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
                          return f.size > maxSize;
                        });
                        if (oversizedFiles.length > 0) {
                          toast.error(`Some files are too large: ${oversizedFiles.map(f => f.name).join(', ')} (Max: 50MB images, 100MB videos)`);
                          return;
                        }
                        
                        // Show progress toast
                        const toastId = toast.loading(`Uploading 0/${files.length} files...`);
                        
                        const uploadedUrls = [];
                        let uploadedCount = 0;
                        let failedCount = 0;
                        
                        // Upload files one by one with progress
                        for (const file of files) {
                          try {
                            const formData = new FormData();
                            formData.append("file", file);
                            const response = await axios.post(`${API}/upload/talent-photo`, formData, {
                              headers: {
                                ...getAuthHeaders(),
                                'Content-Type': 'multipart/form-data'
                              },
                              timeout: 60000 // 60 second timeout per file
                            });
                            uploadedUrls.push(response.data.file_id);
                            uploadedCount++;
                            toast.loading(`Uploading ${uploadedCount}/${files.length} files...`, { id: toastId });
                          } catch (error) {
                            failedCount++;
                            console.error(`Upload failed for ${file.name}:`, error);
                            // Continue with other files
                          }
                        }
                        
                        // Dismiss loading toast
                        toast.dismiss(toastId);
                        
                        if (uploadedUrls.length > 0) {
                          setTalentForm({
                            ...talentForm,
                            photos: [...(talentForm.photos || []), ...uploadedUrls]
                          });
                          
                          if (failedCount > 0) {
                            toast.success(`${uploadedUrls.length} uploaded, ${failedCount} failed`);
                          } else {
                            toast.success(`${uploadedUrls.length} file(s) uploaded!`);
                          }
                        } else {
                          toast.error("All uploads failed. Please try smaller files or fewer at a time.");
                        }
                        e.target.value = '';
                      }}
                    />
                    <label
                      htmlFor="work-upload"
                      className="flex flex-col items-center justify-center gap-1 p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#7B2FBE] hover:bg-purple-50 transition-colors"
                    >
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-500">Click to upload photos/videos</span>
                      <span className="text-xs text-gray-400">(Max 10 files, 50MB images / 100MB videos)</span>
                    </label>
                  </div>
                )}
                
                {/* Video Showreel Link */}
                <div className="mt-3">
                  <Label className="text-xs text-gray-600">Video Showreel Link (YouTube/Vimeo)</Label>
                  <Input
                    placeholder="https://youtube.com/watch?v=..."
                    value={talentForm.video_showreel}
                    onChange={(e) => setTalentForm({...talentForm, video_showreel: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <Button
              onClick={saveTalentProfile}
              disabled={savingTalent}
              className="w-full bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] hover:opacity-90 rounded-xl py-5"
            >
              {savingTalent ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Star Profile</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav userType="influencer" />

      {/* Share Profile Dialog */}
      <ShareProfileDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        star={talentProfile ? {
          user_id: user?.user_id,
          name: talentProfile.name || user?.name || "Star",
          photo_url: talentProfile.picture || (talentProfile.photos && talentProfile.photos[0]) || talentProfile.photo_url || user?.picture || null,
          primary_category: categories.find(c => c.id === talentProfile.category)?.name || talentProfile.category || "Creator",
          categories: talentProfile.categories || [talentProfile.category].filter(Boolean),
          niches: formData.niches || profile?.niches || [],
          bio: talentProfile.bio,
          is_verified: !!talentProfile.is_verified,
          is_pro_creator: !!talentProfile.is_pro_creator,
          // Precise location — prefer talent profile (Star Profile) values, then InfluencerProfile defaults
          location: talentProfile?.location || talentForm.location || formData.location || profile?.location || "",
          village: talentProfile?.village || talentForm.village || formData.village || profile?.village || "",
          city: talentProfile?.city || talentForm.city || formData.city || profile?.city || "",
          district: talentProfile?.district || talentForm.district || formData.district || profile?.district || "",
          state: talentProfile?.state || talentForm.state || formData.state || profile?.state || "",
          stats: {
            followers: talentProfile.followers_count || 0,
            rating: talentProfile.rating || talentProfile.trust_score || 0,
            completed_collabs: talentProfile.bookings_completed || talentProfile.closed_deals || 0,
          },
        } : null}
      />
    </div>
  );
};

export default InfluencerProfile;
